import React, { useState, useMemo, useEffect, useRef } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface TableColumn<T> {
  label: string;
  /** Drawn in the header instead of `label` (a select-all checkbox); not sortable. */
  header?: React.ReactNode;
  /** A column without `width`: the least it takes before the table scrolls sideways (160px). */
  minPx?: number;
  align?: "left" | "right" | "center";
  width?: number | string;
  sortFn?: (item: T) => string | number;
}

export interface BaseTransactionTableProps<T> {
  columns: TableColumn<T>[];
  items: T[];
  renderRow: (item: T) => React.ReactNode;
  emptyMessage?: string;
  defaultRowsPerPage?: number;
  defaultSortColumnIndex?: number;
  defaultSortDirection?: "asc" | "desc";
  /** Tighter padding / fill width — matches denser AI Analysis panels. */
  dense?: boolean;
  /**
   * When set (typically with `dense`), the scroll body is fixed to this many
   * rows of height so the base page size fits without an inner scrollbar;
   * larger page sizes scroll inside that same viewport.
   */
  scrollViewportRows?: number;
  /**
   * Bring a row into view once per `key`: the page holding the first item that matches (in the
   * table's order) opens, and the first row the caller marked `data-highlighted` scrolls into
   * view. Waits until a matching item is in `items`.
   */
  reveal?: { key: string; match: (item: T) => boolean };
  /** Back to the first page whenever this changes (not on mount). */
  pageResetKey?: unknown;
  /**
   * Fill a flex column whose height is only capped (max-height), where `h-full` doesn't resolve:
   * the table, its bordered box and the scrollport flex, so the rows scroll and the pager stays.
   * The parent is a flex column the table can shrink in (`min-h-0`). Doesn't combine
   * with `scrollViewportRows`, which sizes the body to a row count instead.
   */
  fill?: boolean;
}

/** Dense table header (`[&_th]:h-8`). */
export const DENSE_TXN_TABLE_HEADER_PX = 32;
/**
 * Dense row with h-8 category/revenue controls + `[&_td]:py-1.5` + row border.
 * Keep in sync with dense Table / ExpandableRow / ClassificationOverrideSelects.
 */
export const DENSE_TXN_TABLE_ROW_PX = 44;

export function denseTxnTableBodyHeightPx(rows: number): number {
  return DENSE_TXN_TABLE_HEADER_PX + rows * DENSE_TXN_TABLE_ROW_PX;
}

/** Approximate px for column width hints (rem assumes 16px root). */
function columnWidthPx(width?: number | string): number | null {
  if (typeof width === "number") return width;
  if (typeof width !== "string") return null;
  if (width.endsWith("rem")) return parseFloat(width) * 16;
  if (width.endsWith("px")) return parseFloat(width);
  return null;
}

const FLEX_COLUMN_MIN_PX = 160;

const BaseTransactionTable = <T,>({
  columns,
  items,
  renderRow,
  emptyMessage = "No transactions found",
  defaultRowsPerPage = 20,
  defaultSortColumnIndex,
  defaultSortDirection = "desc",
  dense = false,
  scrollViewportRows,
  reveal,
  pageResetKey,
  fill = false,
}: BaseTransactionTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortColIdx, setSortColIdx] = useState<number | null>(defaultSortColumnIndex ?? null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultSortDirection);
  const scrollport = useRef<HTMLDivElement>(null);
  const revealedKey = useRef<string | null>(null);
  const [scrollPending, setScrollPending] = useState(false);
  const lastResetKey = useRef(pageResetKey);

  const denseMinWidthPx = useMemo(() => {
    if (!dense) return undefined;
    return columns.reduce(
      (sum, col) => sum + (columnWidthPx(col.width) ?? col.minPx ?? FLEX_COLUMN_MIN_PX),
      0
    );
  }, [columns, dense]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= items.length) {
      setPage(0);
    }
  }, [items.length, page, rowsPerPage]);

  const handleSort = (colIdx: number) => {
    if (sortColIdx === colIdx) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColIdx(colIdx);
      setSortDir("asc");
    }
    setPage(0);
  };

  const sortedItems = useMemo(() => {
    if (sortColIdx === null) return items;
    const fn = columns[sortColIdx].sortFn;
    if (!fn) return items;
    return [...items].sort((a, b) => {
      const av = fn(a);
      const bv = fn(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [items, sortColIdx, sortDir, columns]);

  useEffect(() => {
    if (Object.is(lastResetKey.current, pageResetKey)) return;
    lastResetKey.current = pageResetKey;
    setPage(0);
  }, [pageResetKey]);

  useEffect(() => {
    if (!reveal || revealedKey.current === reveal.key) return;
    const index = sortedItems.findIndex(reveal.match);
    if (index < 0) return;
    revealedKey.current = reveal.key;
    setPage(Math.floor(index / rowsPerPage));
    setScrollPending(true);
  }, [reveal, sortedItems, rowsPerPage]);

  useEffect(() => {
    if (!scrollPending) return;
    setScrollPending(false);
    scrollport.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "center" });
  }, [scrollPending]);

  const fixedBodyHeightPx =
    dense && scrollViewportRows != null && scrollViewportRows > 0
      ? denseTxnTableBodyHeightPx(scrollViewportRows)
      : null;

  // Callers that size themselves (no row viewport) keep the short message.
  // A fixed viewport has to stay that tall when the filter matches nothing,
  // or the row beside it collapses to the message.
  if (items.length === 0 && fixedBodyHeightPx == null) {
    return (
      <div
        className={cn(
          "py-8 text-center text-muted-foreground",
          dense ? "text-[0.75rem]" : "text-sm"
        )}
      >
        {emptyMessage}
      </div>
    );
  }

  const paginated =
    items.length === 0 ? [] : sortedItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(items.length / rowsPerPage));
  const canPrev = items.length > 0 && page > 0;
  const canNext = items.length > 0 && page < totalPages - 1;

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        fixedBodyHeightPx == null && "h-full",
        fill && "min-h-0 flex-1"
      )}
    >
      {/*
        Border stays outside the measured viewport height so 20 dense rows fit
        without a 2px-border scrollbar.
      */}
      <div
        className={cn(
          "min-w-0 overflow-hidden rounded-md border",
          fixedBodyHeightPx == null && "min-h-0 flex-1",
          fill && "flex min-h-0 flex-1 flex-col"
        )}
      >
        {/*
          Single scrollport: fixed height keeps the horizontal scrollbar at the
          pane bottom (not under the last row). Light muted fill shows in any
          unused viewport below short pages. Nested Table overflow is disabled
          so it cannot shrink-wrap the scrollbar to content height.
        */}
        <div
          ref={scrollport}
          className={cn(
            "min-w-0 overflow-auto bg-muted/40",
            fixedBodyHeightPx == null && "h-full",
            fill && "min-h-0 flex-1"
          )}
          style={fixedBodyHeightPx != null ? { height: fixedBodyHeightPx } : undefined}
        >
          {/*
            Dense: table-fixed + w-full fills the pane (flexible column absorbs leftover).
            minWidth from column hints keeps horizontal scroll when the pane is narrower
            than the columns need.
          */}
          <Table
            containerClassName="overflow-visible"
            className={
              dense
                ? "table-fixed bg-card text-[0.75rem] [&_td]:px-2 [&_td]:py-1.5 [&_th]:h-8 [&_th]:px-2"
                : "bg-card"
            }
            style={denseMinWidthPx != null ? { minWidth: denseMinWidthPx } : undefined}
          >
            <TableHeader>
              <TableRow>
                {columns.map((col, i) => (
                  <TableHead
                    key={i}
                    className={cn(
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center"
                    )}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.header ? (
                      col.header
                    ) : col.sortFn ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "-ml-2 gap-1 px-2 font-medium text-muted-foreground hover:text-foreground",
                          dense ? "h-7 text-[0.75rem]" : "h-8"
                        )}
                        onClick={() => handleSort(i)}
                      >
                        {col.label}
                        {sortColIdx === i ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                        )}
                      </Button>
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="py-8 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map(renderRow)
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-4 border-t bg-card px-1 py-2">
        <div
          className={cn(
            "flex items-center gap-2 text-muted-foreground",
            dense ? "text-[0.75rem]" : "text-sm"
          )}
        >
          <span>Rows per page</span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(v) => {
              setRowsPerPage(Number(v));
              setPage(0);
            }}
          >
            <SelectTrigger size="sm" className="w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className={cn("text-muted-foreground", dense ? "text-[0.75rem]" : "text-sm")}>
          {items.length === 0
            ? "0–0 of 0"
            : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, items.length)} of ${items.length}`}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BaseTransactionTable;
